import React from 'react';
import { useModal } from 'react-modal-hook';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, Icon, Text } from 'components';
import { useFeatureFlags } from 'contexts/featureFlags';
import { theme, toRem } from 'design';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { FeatureFlags, PermissionType } from 'types';
import { hasSitePermission } from 'utils';
import { Title } from '../styles';
import { AIRulesModal } from './components/AIRulesModal';

interface ISuggestRulesProps {}

const Layout = styled.div`
  flex-direction: column;
  align-items: flex-start;
  padding: ${toRem(24)};
  gap: ${toRem(16)};
  background: ${theme.color.primary[25]};
  border: ${toRem(1)} solid ${theme.color.primary[500]};
  border-radius: ${toRem(8)};
  align-self: stretch;
  flex-grow: 0;
  margin-top: ${toRem(20)};

  p {
    color: ${theme.color.neutral[600]};
  }
`;

const StyledButton = styled(Button)`
  width: ${toRem(200)} !important;
`;

const StyledIcon = styled(Icon)`
  position: relative;
  margin-right: ${toRem(8)};
  fill: ${theme.color.primary[600]};
  top: ${toRem(3)};
`;

export const SuggestRules: React.FC<ISuggestRulesProps> = () => {
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const suggestRulesPermission = hasSitePermission(currentSite?.permissions, PermissionType.GENERATE_CALCULATOR_RULES);
  const featureFlags = useFeatureFlags([FeatureFlags.TIP_CALCULATOR_AI_RULES_SUGGEST]);
  const tipCalculatorSuggestRulesEnabled = featureFlags[FeatureFlags.TIP_CALCULATOR_AI_RULES_SUGGEST]?.enabled;

  // Do not display if we do not have permissions or the feature flag is not enabled
  if (!suggestRulesPermission || !tipCalculatorSuggestRulesEnabled) return <></>;

  const [showRulesLoader, hideRulesLoader] = useModal(() => <AIRulesModal siteId={currentSite.id} onClose={hideRulesLoader} />, [currentSite]);

  const handleSuggestRules = () => {
    showRulesLoader();
  };

  return (
    <Layout data-cy="ai-rules-suggestion">
      <Title fontWeight="medium">
        <StyledIcon icon="Wizard" />
        Tip Rules Generator
      </Title>
      <Text size="xsmall">
        Automatically generate tip calculator rules using your point of sale data. Let AI analyze your sales, roles, and tip patterns to suggest fair, consistent rule sets — eliminating guesswork and accelerating setup.
      </Text>
      <StyledButton appearance="primary" size="small" label="Suggest Tip Calculator Rules" data-cy="ai-rules-button" onClick={handleSuggestRules} />
    </Layout>
  );
};

export default SuggestRules;
